import React, { Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import "./manil-review.css";

const MANILG1ReviewerWorkspace = React.lazy(
  () => import("./MANILG1ReviewerWorkspace")
);

function VerificationScreen({ message }) {
  return (
    <main className="manil-session-screen" aria-live="polite">
      <div>
        <LockKeyhole size={28} aria-hidden="true" />
        <p>{message}</p>
      </div>
    </main>
  );
}

export default function MANILG1ProtectedReview() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const controller = new AbortController();
    let expiryTimer;
    let disposed = false;

    const redirectToGate = () => {
      if (disposed) return;
      setState("redirecting");
      navigate("/ukdi-manil-g1-preview", { replace: true });
    };

    const verifySession = async () => {
      try {
        const response = await fetch("/api/manil-g1?action=session", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.authorized) {
          redirectToGate();
          return;
        }
        const expiresAt = Number(payload.expiresAt);
        if (!Number.isInteger(expiresAt)) {
          redirectToGate();
          return;
        }
        const remaining = expiresAt * 1000 - Date.now();
        if (remaining <= 0) {
          redirectToGate();
          return;
        }
        window.clearTimeout(expiryTimer);
        expiryTimer = window.setTimeout(redirectToGate, remaining + 250);
        if (disposed) return;
        setState("authorized");
      } catch (error) {
        if (error.name === "AbortError") return;
        redirectToGate();
      }
    };

    const verifyWhenVisible = () => {
      if (document.visibilityState === "visible") verifySession();
    };

    verifySession();
    window.addEventListener("focus", verifySession);
    document.addEventListener("visibilitychange", verifyWhenVisible);

    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(expiryTimer);
      window.removeEventListener("focus", verifySession);
      document.removeEventListener("visibilitychange", verifyWhenVisible);
    };
  }, [navigate]);

  if (state !== "authorized") {
    return (
      <VerificationScreen
        message={
          state === "redirecting"
            ? "Reviewer session required. Redirecting…"
            : "Verifying reviewer session…"
        }
      />
    );
  }

  return (
    <Suspense fallback={<VerificationScreen message="Opening workspace…" />}>
      <MANILG1ReviewerWorkspace />
    </Suspense>
  );
}
